import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image, Flex, Button} from '@salesforce/retail-react-app/app/components/shared/ui'
import {motion} from 'framer-motion'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import {convertSfccHrefToPwaHref} from '@consteed/frontend/utils/richTextConfigurations'
import Link from '@consteed/overrides/components/link'
import RTE from '@consteed/frontend/components/pageDesigner/RTE'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui/Link'
import {EmailIcon, PhoneIcon} from '@consteed/overrides/components/icons'

// @todo: add responsive behaviour/image-sizes
const Picture = ({image, alt}) => (
    <picture>
        <source srcSet={image?.src?.tablet} media="(min-width: 48em)" />
        <source srcSet={image?.src?.desktop} media="(min-width: 64em)" />
        <Image
            data-testid={'imageTextButton-image'}
            src={image?.src?.mobile ? image?.src?.mobile : image?.url}
            ignoreFallback={true}
            alt={alt}
            title={alt}
        />
    </picture>
)
Picture.propTypes = {
    image: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    alt: PropTypes.string
}

/**
 * ContactButton component
 *
 * @param {object} props
 * @param {string} - props.buttonLink - Button Link.
 * @param {string} - props.buttonText - Button Text.
 * @param {string} - props.type - tel | email.
 * @returns {React.ReactElement} - ContactButton component.
 */
const ContactButton = ({buttonLink, buttonText, type}) => {
    const pwabuttonLink = convertSfccHrefToPwaHref(buttonLink)
    const isAbsolute = isAbsoluteURL(pwabuttonLink)
    const LinkWrapper = isAbsolute ? ChakraLink : Link
    const linkProps = isAbsolute ? {href: pwabuttonLink} : {to: pwabuttonLink}

    return (
        <LinkWrapper {...linkProps}>
            <Button
                variant={'primary'}
                size={'md'}
                rightIcon={type === 'tel' ? <PhoneIcon /> : <EmailIcon />}
                width={{base: 'full', md: 'inherit'}}
                mt={5}
            >
                {buttonText}
            </Button>
        </LinkWrapper>
    )
}
ContactButton.propTypes = {
    buttonLink: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired
}

const MotionBox = motion(Box)
const variants = {
    slideIn: {
        // opacity: 1,
        y: 0,
        transition: {
            ease: 'easeInOut',
            duration: 1
        }
    },
    slideInHorse: {
        y: 0,
        transition: {
            ease: 'easeInOut',
            duration: 0.8
        }
    },
    hiddenHorse: {
        y: '30%'
    },
    hiddenButton: {
        y: '40%'
    },
    hiddenText: {
        y: '50%'
    }
}

/**
 * LandingPageQuestions component
 *
 * @param {object} props
 * @param {image} - props.image - Image.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @param {string} - props.headline - Headline beside Image.
 * @param {string} - props.text - Text beside Image.
 * @param {string} - props.button1Link - Button 1 Link.
 * @param {string} - props.button1Text - Button 1 Text.
 * @param {string} - props.button2Link - Button 2 Link.
 * @param {string} - props.button2Text - Button 2 Text.
 * @returns {React.ReactElement} - LandingPageQuestions component.
 */
const LandingPageQuestions = ({
    image,
    alt,
    headline,
    text,
    button1Link,
    button1Text,
    button2Link,
    button2Text
}) => {
    return (
        <Flex
            className={'pdcOuterBox'}
            direction={{base: 'column', md: 'row'}}
            align="center"
            gap={4}
        >
            <Box flex="1" mb={{base: 4, md: 0}}>
                <MotionBox variants={variants} initial="hiddenText" whileInView="slideIn">
                    <RTE richText={headline} />
                    <RTE richText={text} />
                </MotionBox>
                <Flex gap={5}>
                    <ContactButton buttonText={button1Text} buttonLink={button1Link} type={'tel'} />
                    <MotionBox variants={variants} initial="hiddenButton" whileInView="slideIn">
                        <ContactButton
                            buttonText={button2Text}
                            buttonLink={button2Link}
                            type={'email'}
                        />
                    </MotionBox>
                </Flex>
            </Box>
            <MotionBox variants={variants} initial="hiddenHorse" whileInView="slideIn" flex="1">
                <Picture image={image} alt={alt} />
            </MotionBox>
        </Flex>
    )
}

LandingPageQuestions.propTypes = {
    image: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string,
        src: PropTypes.string || PropTypes.object
    }).isRequired,
    alt: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    button1Link: PropTypes.string.isRequired,
    button1Text: PropTypes.string.isRequired,
    button2Link: PropTypes.string.isRequired,
    button2Text: PropTypes.string.isRequired
}

export default LandingPageQuestions
